import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { YearMonthCalendar } from "@/components/ui/year-month-calendar";
import { format } from "date-fns";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { License, insertLicenseSchema, Application, PaymentCard, Employee } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { CalendarIcon, CreditCard, Loader2, AlertTriangle, Info, Check, ChevronsUpDown, UserPlus, X, Users, FileText } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { usePaymentCards } from "@/hooks/use-payment-cards";
import { z } from "zod";
import { useAppSettings } from "@/hooks/use-app-settings";
import { AppSettingSelect } from "@/components/ui/app-setting-select";
import { toUTCDateString } from "@/lib/date-utils";

type EditLicenseDialogProps = {
  licenseId: number | null;
  isOpen: boolean;
  onClose: () => void;
};

// Dynamic schema for license editing based on historical data mode
const createEditLicenseSchema = (isHistoricalData: boolean) => {
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  return z.object({
    applicationId: z.number(),
    name: z.string(),
    totalSeats: z.number().min(1, "Must have at least 1 seat"),
    assignedSeats: z.number(),
    cost: z.number(),
    billingCycle: z.string(),
    startDate: z.date().refine((date) => {
      if (isHistoricalData) return true; // Allow any date for historical data
      return date >= thirtyDaysAgo; // Allow up to 30 days in past for grace period
    }, {
      message: isHistoricalData 
        ? "Start date is required" 
        : "Start date cannot be more than 30 days in the past (enable 'Historical Data' for older dates)"
    }),
    renewalDate: z.date(),
    paymentCardId: z.string().optional()
  }).refine((data) => {
    return data.renewalDate > data.startDate;
  }, {
    message: "Renewal date must be after start date",
    path: ["renewalDate"],
  }).refine((data) => {
    return data.totalSeats >= data.assignedSeats;
  }, {
    message: "Total seats cannot be less than the number of already-assigned seats",
    path: ["totalSeats"],
  });
};

export default function EditLicenseDialog({
  licenseId,
  isOpen,
  onClose,
}: EditLicenseDialogProps) {
  const { toast } = useToast();
  const localQueryClient = useQueryClient();
  const [selectedPaymentCardId, setSelectedPaymentCardId] = useState<string | null>(null);
  const [isHistoricalData, setIsHistoricalData] = useState(false);
  const [appComboOpen, setAppComboOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [assignPanelOpen, setAssignPanelOpen] = useState(false);
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [productOwnerId, setProductOwnerId] = useState<number | null>(null);
  const [ownerComboOpen, setOwnerComboOpen] = useState(false);
  const { 
    cards, 
    isLoadingCards, 
    assignCardToLicenseMutation, 
    getLicenseCard
  } = usePaymentCards();
  
  // Fetch application data for the dropdown (sorted alphabetically)
  const { data: applicationsRaw = [], isLoading: appsLoading } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
    enabled: isOpen,
  });
  const applications = [...applicationsRaw].sort((a, b) => a.name.localeCompare(b.name));
  
  // Fetch license data if we have an ID
  const { data: license, isLoading: licenseLoading } = useQuery<License>({
    queryKey: ['/api/licenses', licenseId],
    queryFn: async () => {
      if (!licenseId) throw new Error("License ID is required");
      const res = await fetch(`/api/licenses/${licenseId}`);
      if (!res.ok) {
        throw new Error('Failed to fetch license');
      }
      return res.json();
    },
    enabled: isOpen && licenseId !== null,
  });
  
  // Fetch the payment card assigned to this license
  const { data: paymentCard, isLoading: cardLoading } = useQuery({
    queryKey: ['/api/licenses', licenseId, 'payment-card'],
    queryFn: async () => {
      if (!licenseId) return null;
      try {
        const cardData = await getLicenseCard(licenseId);
        return cardData;
      } catch (error) {
        console.error("Error fetching license payment card:", error);
        return null;
      }
    },
    enabled: isOpen && licenseId !== null,
  });
  
  // Set the selected payment card ID when the card data is loaded
  useEffect(() => {
    if (paymentCard && paymentCard.id) {
      setSelectedPaymentCardId(paymentCard.id.toString());
    } else {
      setSelectedPaymentCardId(null);
    }
  }, [paymentCard]);

  // Employee queries
  const { data: assignedEmployees = [], isLoading: empLoading } = useQuery<Employee[]>({
    queryKey: ['/api/licenses', licenseId, 'employees'],
    queryFn: async () => {
      const res = await fetch(`/api/licenses/${licenseId}/employees`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: isOpen && licenseId !== null,
  });

  const { data: allEmployees = [] } = useQuery<Employee[]>({
    queryKey: ['/api/employees'],
    enabled: isOpen && licenseId !== null,
  });

  const assignEmpMutation = useMutation({
    mutationFn: async (employeeId: number) => {
      const res = await fetch('/api/license-employees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseId, employeeId }),
      });
      if (!res.ok) throw new Error('Failed to assign employee');
      return res.json();
    },
    onSuccess: () => {
      localQueryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId, 'employees'] });
      localQueryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId] });
      localQueryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      localQueryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      setSelectedEmployee(null);
      toast({ title: "Employee assigned" });
    },
    onError: () => toast({ title: "Failed to assign employee", variant: "destructive" }),
  });

  const removeEmpMutation = useMutation({
    mutationFn: async (employeeId: number) => {
      const res = await fetch('/api/license-employees', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseId, employeeId }),
      });
      if (!res.ok) throw new Error('Failed to remove employee');
      return res.json();
    },
    onSuccess: () => {
      localQueryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId, 'employees'] });
      localQueryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId] });
      localQueryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      localQueryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      toast({ title: "Employee removed" });
    },
    onError: () => toast({ title: "Failed to remove employee", variant: "destructive" }),
  });

  // Keep assignedSeats form field in sync with actual assignments
  useEffect(() => {
    form.setValue('assignedSeats', assignedEmployees.length);
  }, [assignedEmployees.length]);
  
  // Helper to recalculate renewal date from start date + billing cycle
  const calculateRenewalDate = (startDate: Date, billingCycle: string): Date => {
    const renewalDate = new Date(startDate);
    switch (billingCycle.toLowerCase()) {
      case "monthly":
        renewalDate.setMonth(renewalDate.getMonth() + 1);
        break;
      case "quarterly":
        renewalDate.setMonth(renewalDate.getMonth() + 3);
        break;
      case "semi-annual":
      case "half-yearly":
      case "half yearly":
        renewalDate.setMonth(renewalDate.getMonth() + 6);
        break;
      case "yearly":
      case "annual":
        renewalDate.setFullYear(renewalDate.getFullYear() + 1);
        break;
      case "biennial":
        renewalDate.setFullYear(renewalDate.getFullYear() + 2);
        break;
      case "one-time":
      case "lifetime":
        renewalDate.setFullYear(renewalDate.getFullYear() + 50);
        break;
      default:
        renewalDate.setMonth(renewalDate.getMonth() + 1);
    }
    return renewalDate;
  };

  // Generate the schema based on historical data mode
  const editLicenseSchema = createEditLicenseSchema(isHistoricalData);

  const form = useForm({
    resolver: zodResolver(editLicenseSchema),
    defaultValues: {
      applicationId: 0,
      name: "",
      totalSeats: 1,
      assignedSeats: 0,
      cost: 0,
      billingCycle: "monthly",
      startDate: new Date(),
      renewalDate: new Date(),
      paymentCardId: ""
    }
  });
  
  // Auto-update renewal date when billing cycle or start date changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if ((name === "startDate" || name === "billingCycle") && value.startDate && value.billingCycle) {
        const newRenewalDate = calculateRenewalDate(new Date(value.startDate), value.billingCycle);
        form.setValue("renewalDate", newRenewalDate);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Update form validation when historical data mode changes
  useEffect(() => {
    form.trigger();
  }, [isHistoricalData, form]);

  // Update form values when license data is loaded
  useEffect(() => {
    if (license) {
      const licenseStartDate = new Date(license.startDate);
      // Check if this is historical data (older than 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      if (licenseStartDate < thirtyDaysAgo) {
        setIsHistoricalData(true);
      }
      
      form.reset({
        applicationId: license.applicationId,
        name: license.name,
        totalSeats: license.totalSeats,
        assignedSeats: license.assignedSeats,
        cost: license.cost,
        billingCycle: license.billingCycle,
        startDate: new Date(license.startDate),
        renewalDate: new Date(license.renewalDate),
        paymentCardId: selectedPaymentCardId || ""
      });
      setProductOwnerId((license as any).productOwnerId ?? null);
    }
  }, [license, form, selectedPaymentCardId]);
  
  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!licenseId) throw new Error("License ID is required");
      return await apiRequest(`/api/licenses/${licenseId}`, { 
        method: "PUT", 
        body: data 
      });
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      queryClient.invalidateQueries({ queryKey: ['/api/renewals'] });
      
      // Show success message
      toast({
        title: "License updated",
        description: "The license details have been updated successfully.",
      });
      
      // Close the dialog
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to update license: ${error.message}`,
        variant: "destructive"
      });
    }
  });
  
  function onSubmit(data: any) {
    // Convert string values to appropriate types and ensure dates are properly formatted
    try {
      // Properly handle the dates
      let startDate, renewalDate;
      
      if (data.startDate instanceof Date) {
        startDate = data.startDate;
      } else {
        startDate = new Date(data.startDate);
        if (isNaN(startDate.getTime())) {
          startDate = new Date(); // Fallback to today
        }
      }
      
      if (data.renewalDate instanceof Date) {
        renewalDate = data.renewalDate;
      } else {
        renewalDate = new Date(data.renewalDate);
        if (isNaN(renewalDate.getTime())) {
          renewalDate = new Date();
          renewalDate.setMonth(renewalDate.getMonth() + 1); // Fallback to one month from today
        }
      }
      
      // Ensure cost is properly converted to a number
      let cost = data.cost;
      if (typeof cost === 'string') {
        cost = cost === '' ? 0 : Number(cost);
        if (isNaN(cost)) {
          throw new Error('Cost must be a valid number');
        }
      }

      const formattedData = {
        ...data,
        applicationId: Number(data.applicationId),
        totalSeats: Number(data.totalSeats),
        assignedSeats: Number(data.assignedSeats),
        cost: cost,
        billingCycle: data.billingCycle ? data.billingCycle.toLowerCase() : data.billingCycle,
        startDate: toUTCDateString(startDate),
        renewalDate: toUTCDateString(renewalDate),
        productOwnerId: productOwnerId ?? null,
      };
      
      console.log("Submitting license update:", formattedData);
      updateMutation.mutate(formattedData, {
        onSuccess: (createdLicense) => {
          // Check if payment card has been updated or newly assigned
          // Only update payment card association if the card ID has changed from the initial value
          const initialCardId = paymentCard?.id ? paymentCard.id.toString() : null;
          const hasCardChanged = selectedPaymentCardId !== initialCardId;
          
          if (hasCardChanged && selectedPaymentCardId && licenseId) {
            try {
              console.log("Payment card changed, updating association");
              const paymentCardId = parseInt(selectedPaymentCardId);
              if (!isNaN(paymentCardId)) {
                assignCardToLicenseMutation.mutate({
                  licenseId: licenseId,
                  paymentCardId: paymentCardId,
                  isActive: true
                });
              }
            } catch (error) {
              console.error("Error associating payment card with license:", error);
              toast({
                title: "Warning",
                description: "License was updated but there was an issue associating the payment card.",
                variant: "destructive"
              });
            }
          }
        }
      });
    } catch (error) {
      console.error("Error formatting license data:", error);
      toast({
        title: "Error",
        description: "There was a problem formatting the license data. Please check the dates and try again.",
        variant: "destructive"
      });
    }
  }
  
  const isLoading = licenseLoading || appsLoading;
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit License</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-2">
            <TabsTrigger value="details" className="flex items-center gap-1.5">
              <FileText className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="employees" className="flex items-center gap-1.5">
              <Users className="h-4 w-4" />
              Employees
              {assignedEmployees.length > 0 && (
                <span className="ml-1 bg-primary/10 text-primary rounded-full px-1.5 py-0.5 text-xs font-medium">
                  {assignedEmployees.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details">
        {isLoading ? (
          <div className="space-y-4 my-4">
            <div className="space-y-2">
              <div className="h-4 w-[30%] bg-slate-200 rounded animate-pulse"></div>
              <div className="h-10 w-full bg-slate-200 rounded animate-pulse"></div>
            </div>
            <div className="space-y-2">
              <div className="h-4 w-[30%] bg-slate-200 rounded animate-pulse"></div>
              <div className="h-10 w-full bg-slate-200 rounded animate-pulse"></div>
            </div>
            <div className="space-y-2">
              <div className="h-4 w-[30%] bg-slate-200 rounded animate-pulse"></div>
              <div className="h-10 w-full bg-slate-200 rounded animate-pulse"></div>
            </div>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-2">
              <FormField
                control={form.control}
                name="applicationId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application*</FormLabel>
                    <Popover open={appComboOpen} onOpenChange={setAppComboOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            className={cn("w-full justify-between font-normal", !field.value && "text-muted-foreground")}
                          >
                            {field.value
                              ? applications.find(a => a.id === field.value)?.name ?? "Select application"
                              : "Select application"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search applications..." />
                          <CommandList>
                            <CommandEmpty>No application found.</CommandEmpty>
                            <CommandGroup>
                              {applications.map((app: Application) => (
                                <CommandItem
                                  key={app.id}
                                  value={app.name}
                                  onSelect={() => {
                                    field.onChange(app.id);
                                    setAppComboOpen(false);
                                  }}
                                >
                                  <Check className={cn("mr-2 h-4 w-4", field.value === app.id ? "opacity-100" : "opacity-0")} />
                                  {app.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>License Name*</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Enterprise Plan" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="totalSeats"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Seats*</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min="1" 
                          onChange={(e) => {
                            // Convert string to number before updating field
                            const value = e.target.value === '' ? '0' : e.target.value;
                            field.onChange(Number(value));
                          }}
                          value={field.value}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="assignedSeats"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1">
                        Assigned Seats
                        <span className="text-xs text-muted-foreground font-normal">(auto-managed)</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          readOnly
                          className="bg-slate-50 cursor-not-allowed text-muted-foreground"
                          value={field.value}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">Updated automatically when employees are assigned.</p>
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="cost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Subscription Cost*</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                            $
                          </span>
                          <Input 
                            type="text"
                            className="pl-8"
                            placeholder="0.00"
                            value={field.value}
                            onChange={(e) => {
                              // Only allow numbers and decimal point
                              const value = e.target.value.replace(/[^0-9.]/g, '');
                              // Prevent multiple decimal points
                              const parts = value.split('.');
                              const formatted = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                              field.onChange(parseFloat(formatted) || 0);
                              e.target.value = formatted;
                            }}
                          />
                        </div>
                      </FormControl>
                      <p className="text-xs text-muted-foreground mt-1">Enter the total cost for the entire subscription, not per seat</p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="billingCycle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Billing Cycle <span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select billing cycle" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Monthly">Monthly</SelectItem>
                            <SelectItem value="Quarterly">Quarterly</SelectItem>
                            <SelectItem value="Semi-Annual">Semi-Annual</SelectItem>
                            <SelectItem value="Annual">Annual</SelectItem>
                            <SelectItem value="Biennial">Biennial</SelectItem>
                            <SelectItem value="One-Time">One-Time</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Historical Data Toggle */}
              <div className="flex items-center space-x-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Switch
                  id="historical-data-edit"
                  checked={isHistoricalData}
                  onCheckedChange={setIsHistoricalData}
                />
                <div className="flex-1">
                  <label htmlFor="historical-data-edit" className="text-sm font-medium text-blue-900 cursor-pointer">
                    Editing historical data
                  </label>
                  <p className="text-xs text-blue-700">
                    Enable this to modify licenses from the past without date validation
                  </p>
                </div>
                <Info className="h-4 w-4 text-blue-600" />
              </div>

              {/* Warning for historical data */}
              {isHistoricalData && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    You're editing historical data. Date validations are disabled - please verify all dates are correct.
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Start Date*</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className={"pl-3 text-left font-normal"}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <YearMonthCalendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            month={field.value}
                            onMonthChange={(newMonth) => {
                              // This lets us handle the month/year selection
                              const currentDate = new Date(field.value);
                              currentDate.setFullYear(newMonth.getFullYear());
                              currentDate.setMonth(newMonth.getMonth());
                              field.onChange(currentDate);
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="renewalDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Renewal Date*</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className={"pl-3 text-left font-normal"}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <YearMonthCalendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            month={field.value}
                            onMonthChange={(newMonth) => {
                              // This lets us handle the month/year selection
                              const currentDate = new Date(field.value);
                              currentDate.setFullYear(newMonth.getFullYear());
                              currentDate.setMonth(newMonth.getMonth());
                              field.onChange(currentDate);
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              
              {/* Payment Card Selection */}
              <FormField
                control={form.control}
                name="paymentCardId" 
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assign Payment Card</FormLabel>
                    {isLoadingCards || cardLoading ? (
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading payment cards...
                      </div>
                    ) : (
                      <Select 
                        onValueChange={(value) => {
                          setSelectedPaymentCardId(value);
                          field.onChange(value);
                        }}
                        value={selectedPaymentCardId || ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select payment card" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {cards && cards.length > 0 ? (
                            cards.map((card: PaymentCard) => (
                              <SelectItem 
                                key={card.id} 
                                value={card.id ? card.id.toString() : ''}
                              >
                                <div className="flex items-center">
                                  <span>{card.cardName} - {card.cardProvider} •••• {card.lastFourDigits}</span>
                                  {card.isDefault && (
                                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary font-medium">
                                      Default
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem disabled value="none">No payment cards available</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {/* Product Owner */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Product Owner</label>
                <Popover open={ownerComboOpen} onOpenChange={setOwnerComboOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                      {productOwnerId
                        ? allEmployees.find(e => e.id === productOwnerId)?.fullName ?? "Select owner"
                        : "Select owner"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search people..." />
                      <CommandList>
                        <CommandEmpty>No people found.</CommandEmpty>
                        <CommandGroup>
                          {productOwnerId && (
                            <CommandItem value="__clear__" onSelect={() => { setProductOwnerId(null); setOwnerComboOpen(false); }}>
                              <X className="mr-2 h-4 w-4 text-muted-foreground" /> Clear selection
                            </CommandItem>
                          )}
                          {allEmployees.map((emp) => (
                            <CommandItem
                              key={emp.id}
                              value={emp.fullName}
                              onSelect={() => { setProductOwnerId(emp.id); setOwnerComboOpen(false); }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", productOwnerId === emp.id ? "opacity-100" : "opacity-0")} />
                              <div className="flex flex-col">
                                <span className="font-medium">{emp.fullName}</span>
                                {emp.jobTitle && <span className="text-xs text-muted-foreground">{emp.jobTitle}</span>}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <DialogFooter className="pt-4 flex-col sm:flex-row sm:justify-between w-full">
                {/* Left side action buttons */}
                <div className="flex flex-col sm:flex-row gap-2 mb-4 sm:mb-0 w-full sm:w-auto">
                  <Button 
                    type="button" 
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={async () => {
                      console.log("🔵 Mark as Renewed button clicked");
                      
                      if (!licenseId) {
                        toast({
                          title: "Error",
                          description: "License ID is missing",
                          variant: "destructive"
                        });
                        return;
                      }

                      try {
                        console.log("🔵 Calling /api/renewals/mark-renewed for license:", licenseId);
                        
                        // Call the renewal endpoint to create historical record
                        const response = await apiRequest(`/api/renewals/mark-renewed`, {
                          method: 'POST',
                          body: { licenseIds: [licenseId] }
                        });
                        
                        console.log("🔵 Renewal response:", response);
                        
                        // Invalidate all relevant caches
                        await queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
                        await queryClient.invalidateQueries({ queryKey: ['/api/renewals'] });
                        await queryClient.invalidateQueries({ queryKey: ['/api/historical-renewals'] });
                        await queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
                        
                        toast({
                          title: "License Renewed",
                          description: "License has been marked as renewed and added to billing history.",
                        });
                        
                        onClose();
                      } catch (error: any) {
                        console.error("🔴 Error marking license as renewed:", error);
                        toast({
                          title: "Error",
                          description: error.message || "Failed to mark license as renewed",
                          variant: "destructive"
                        });
                      }
                    }}
                    disabled={updateMutation.isPending}
                  >
                    Mark as Renewed
                  </Button>
                  
                  {['discontinued', 'inactive', 'deactivated'].includes((license?.status || '').toLowerCase()) ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:w-auto text-green-600 hover:text-green-700 border-green-300 hover:bg-green-50 hover:border-green-500"
                      onClick={async () => {
                        try {
                          await apiRequest(`/api/licenses/${licenseId}/reactivate`, {
                            method: "POST"
                          });

                          await queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
                          await queryClient.invalidateQueries({ queryKey: ['/api/renewals'] });
                          await queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });

                          toast({
                            title: "License Reactivated",
                            description: "License has been reactivated. A notification email has been sent.",
                          });

                          onClose();
                        } catch (error) {
                          toast({
                            title: "Error",
                            description: "Failed to reactivate license",
                            variant: "destructive",
                          });
                        }
                      }}
                      disabled={updateMutation.isPending}
                    >
                      Reactivate License
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:w-auto text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10 hover:border-destructive"
                      onClick={async () => {
                        // For discontinued licenses, we need to make a special API call to handle the discontinued status
                        try {
                          await apiRequest(`/api/licenses/${licenseId}/discontinue`, {
                            method: "POST"
                          });

                          // Refresh license data after discontinue
                          await queryClient.invalidateQueries({
                            queryKey: ['/api/licenses']
                          });

                          toast({
                            title: "License Discontinued",
                            description: "License has been marked as discontinued and will no longer appear in renewals.",
                          });

                          onClose();
                        } catch (error) {
                          toast({
                            title: "Error",
                            description: "Failed to discontinue license",
                            variant: "destructive",
                          });
                        }
                      }}
                      disabled={updateMutation.isPending}
                    >
                      Discontinue License
                    </Button>
                  )}
                </div>
                
                {/* Right side form control buttons */}
                <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto">
                  <Button 
                    type="button" 
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={onClose}
                    disabled={updateMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="w-full sm:w-auto"
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Updating...
                      </>
                    ) : (
                      "Update License"
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        )}
          </TabsContent>

          {/* Employees Tab */}
          <TabsContent value="employees" className="py-2">
            <div className="space-y-4">
              {/* Seat summary */}
              {license && (
                <div className="flex items-center justify-between p-3 bg-slate-50 border rounded-md text-sm">
                  <span className="text-muted-foreground">
                    {assignedEmployees.length} of {license.totalSeats} seats used
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setAssignPanelOpen(!assignPanelOpen); setSelectedEmployee(null); }}
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    {assignPanelOpen ? 'Cancel' : 'Assign Employee'}
                  </Button>
                </div>
              )}

              {/* Assignment panel */}
              {assignPanelOpen && license && (
                <div className="p-3 border rounded-md bg-blue-50 space-y-3">
                  <div className="flex gap-2">
                    <Popover open={employeeSearchOpen} onOpenChange={setEmployeeSearchOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="flex-1 justify-between text-sm font-normal bg-white">
                          {selectedEmployee ? selectedEmployee.fullName : 'Search employees...'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[320px] p-0">
                        <Command>
                          <CommandInput placeholder="Search by name or email..." />
                          <CommandList>
                            <CommandEmpty>No employees found.</CommandEmpty>
                            <CommandGroup>
                              {allEmployees
                                .filter(emp => !assignedEmployees.some(ae => ae.id === emp.id))
                                .map(emp => (
                                  <CommandItem
                                    key={emp.id}
                                    value={`${emp.fullName} ${emp.email}`}
                                    onSelect={() => { setSelectedEmployee(emp); setEmployeeSearchOpen(false); }}
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", selectedEmployee?.id === emp.id ? "opacity-100" : "opacity-0")} />
                                    <div className="flex flex-col">
                                      <span className="font-medium">{emp.fullName}</span>
                                      <span className="text-xs text-muted-foreground">{emp.email}</span>
                                    </div>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <Button
                      size="sm"
                      disabled={!selectedEmployee || assignEmpMutation.isPending || (license && assignedEmployees.length >= license.totalSeats)}
                      onClick={() => { if (selectedEmployee) assignEmpMutation.mutate(selectedEmployee.id); }}
                    >
                      {assignEmpMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Assign'}
                    </Button>
                  </div>
                  {license && assignedEmployees.length >= license.totalSeats && (
                    <p className="text-xs text-red-500">No available seats. Increase total seats to assign more employees.</p>
                  )}
                </div>
              )}

              {/* Assigned employees list */}
              {empLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : assignedEmployees.length > 0 ? (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {assignedEmployees.map(emp => (
                    <div key={emp.id} className="flex items-center p-2.5 bg-slate-50 border rounded-md text-sm">
                      <div className="bg-primary/10 text-primary rounded-full h-8 w-8 flex items-center justify-center mr-2.5 flex-shrink-0 font-medium text-sm">
                        {emp.fullName ? emp.fullName.charAt(0).toUpperCase() : 'E'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{emp.fullName}</div>
                        <div className="text-xs text-muted-foreground truncate">{emp.email}</div>
                      </div>
                      {emp.jobTitle && (
                        <span className="text-xs bg-slate-200 rounded-full px-2 py-0.5 mr-2 hidden sm:inline">
                          {emp.jobTitle}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        disabled={removeEmpMutation.isPending}
                        onClick={() => removeEmpMutation.mutate(emp.id)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-8 border rounded-md bg-slate-50">
                  <Users className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-muted-foreground">No employees assigned to this license.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => setAssignPanelOpen(true)}
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    Assign First Employee
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}